import React, { forwardRef, type ReactNode } from 'react';
import { CircleHelp } from 'lucide-react';

import { cn } from '@/shared/libs/utils/cn';
import { TooltipSimple } from '@/shared/ui/tooltip-simple';

import { FieldError, Label } from '../../primitives';

export interface FormInputProps extends Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'defaultValue' | 'size' | 'value'
> {
  value?: string | number | null;
  /** Plain string or custom node (e.g. icon + text + required mark). */
  label?: ReactNode;
  /** Tooltip-иконка (CircleHelp) после label */
  tooltipContent?: string;
  error?: string | string[];
  appendSlot?: React.ReactNode;
  prependSlot?: React.ReactNode;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      appendSlot,
      className,
      error,
      id,
      label,
      name,
      onChange,
      prependSlot,
      style,
      tooltipContent,
      value,
      ...props
    },
    ref,
  ) => {
    const errorMessage = Array.isArray(error) ? error[0] : error;
    const hasError = !!errorMessage;
    const inputId = id || name;
    const showLabel =
      label != null && (typeof label !== 'string' || label.length > 0);

    const inputClasses = cn(
      'flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-text',
      'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
      'placeholder:text-muted-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'transition-colors',
      hasError && 'border-destructive focus-visible:ring-destructive',
      prependSlot && 'pl-10',
      appendSlot && 'pr-10',
      className,
    );

    return (
      <div className="flex w-full flex-col gap-1.5">
        {(showLabel || tooltipContent) && (
          <div className="flex items-center gap-2">
            {showLabel && (
              <Label htmlFor={inputId} className="flex items-center gap-2">
                {label}
              </Label>
            )}
            {!!tooltipContent && (
              <TooltipSimple
                content={<p className="text-xs">{tooltipContent}</p>}>
                <CircleHelp className="h-4 w-4 shrink-0 cursor-help text-muted-foreground" />
              </TooltipSimple>
            )}
          </div>
        )}
        <div className="relative">
          {prependSlot && (
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
              {prependSlot}
            </div>
          )}
          <input
            ref={ref}
            type="text"
            id={inputId}
            name={name}
            className={inputClasses}
            value={value ?? ''}
            onChange={onChange}
            style={style}
            {...props}
          />
          {appendSlot && (
            <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1">
              {appendSlot}
            </div>
          )}
        </div>
        <FieldError error={error} />
      </div>
    );
  },
);

FormInput.displayName = 'FormInput';
